import { useEffect, useState } from "react";

import { DashboardFilterType, GetWinnerTopBuyersItem } from "@/types/dashboard";

import { getDashboardProjectFilterToString } from "@/consts/Dashboard";

import { formatKoreanCurrencyWithWon } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";
import { downloadExcel, getExcelFileName } from "@/utils/excelUtils";

import { GetWinnerTopBuyers } from "@/services/Dashboard";

import DashboardCardHeader from "@/components/DashboardCardHeader";

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardContractedCompanyPurchase({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<GetWinnerTopBuyersItem[]>([]);

  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = getDashboardProjectFilterToString(filter);
      const result = await GetWinnerTopBuyers(params);

      if (result.data.success) {
        setData(result.data.pods[0].content.items);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error?.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);

  const handleExcelDownload = () => {
    if (data.length === 0) {
      showToast({ type: "error", message: "다운로드할 데이터가 없습니다." });
      return;
    }

    const columns = [
      { header: "낙찰 업체", key: "winner_company_name", width: 30 },
      { header: "순위", key: "rank", width: 10 },
      { header: "구매 업체", key: "buyer", width: 15 },
      { header: "금액", key: "total_sales_amount", width: 20 },
    ];

    // 낙찰 업체 + buyers를 플랫하게 변환
    const excelData = data.flatMap((item) =>
      item.buyers.map((buyer) => ({
        winner_company_name: item.winner_company_name,
        rank: buyer.rank,
        buyer: buyer.buyer,
        total_sales_amount: buyer.total_sales_amount,
      }))
    );

    downloadExcel(
      excelData,
      columns,
      getExcelFileName("낙찰업체_구매업체_비율")
    );
  };

  return (
    <DashboardCardHeader
      title={title}
      isLoading={isLoading}
      rightElement={
        <button
          className="flex items-center gap-1 text-xs text-gray-A hover:text-gray-600"
          onClick={handleExcelDownload}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.66666 6.66669L7.99999 10L11.3333 6.66669"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 10V2"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          EXCEL 다운로드
        </button>
      }
    >
      <div className="-mx-7 -mb-5">
        <table className="w-full text-sm">
          <thead className="bg-dark-red-60 text-white text-left text-sm font-semibold sticky top-0">
            <tr>
              <th className="px-4 py-2 w-[60%]">낙찰 업체</th>
              <th className="px-4 py-2 text-right w-[10%]">순위</th>
              <th className="px-4 py-2 text-center w-[15%]">구매 업체</th>
              <th className="px-4 py-2 text-right w-[15%]">금액</th>
            </tr>
          </thead>
          <tbody className="text-gray-A text-xs font-normal">
            {data.map((item, itemIndex) =>
              item.buyers.map((buyer, buyerIndex) => (
                <tr
                  key={`${itemIndex}-${buyerIndex}`}
                  style={{
                    borderBottom:
                      buyerIndex === item.buyers.length - 1
                        ? "1px solid var(--Grey-B, #9FA1A2)"
                        : "1px solid var(--Grey-E, #ECEEF0)",
                  }}
                >
                  {buyerIndex === 0 && (
                    <td
                      className="px-4 py-2 align-middle"
                      rowSpan={item.buyers.length}
                    >
                      {item.winner_company_name}
                    </td>
                  )}
                  <td className="px-4 py-2 text-right">{buyer.rank}</td>
                  <td className="px-4 py-2 text-center">{buyer.buyer}</td>
                  <td className="px-4 py-2 text-right">
                    {formatKoreanCurrencyWithWon(buyer.total_sales_amount)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </DashboardCardHeader>
  );
}
